"use client";

import { FocalPointImage } from "@/components/focal-point-image";
import { Autoplay, EffectFade, A11y } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-fade";

import { Icon } from "@/components/icon";
import messages from "@/messages/en.json";
import type { HeroImage } from "../hero-sticky/hero-sticky.interface";

export interface GoogleReview {
  name: string;
  rating: number;
  count: number;
}

interface HeroStickyGalleryProps {
  images: HeroImage[];
  googleReview?: GoogleReview;
  border?: boolean;
}

export function HeroStickyGallery({
  images,
  googleReview,
  border,
}: HeroStickyGalleryProps) {
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const slides =
    images.length === 1 ? (
      <FocalPointImage
        src={images[0].url}
        alt={images[0].alt ?? ""}
        focus={images[0].focus}
        loading="eager"
        fill
        preload
        sizes="(max-width: 1024px) 100vw, 50vw"
        className="object-cover"
      />
    ) : (
      <Swiper
        modules={[Autoplay, EffectFade, A11y]}
        a11y={{
          containerMessage: messages.hero.gallery.containerLabel,
          containerRoleDescriptionMessage:
            messages.hero.gallery.containerRoleDescription,
        }}
        effect="fade"
        autoplay={
          prefersReducedMotion
            ? false
            : { delay: 5000, disableOnInteraction: false }
        }
        loop
        speed={800}
        className="h-full w-full"
      >
        {images.slice(0, 8).map((image, index) => (
          <SwiperSlide key={image.url}>
            <FocalPointImage
              src={image.url}
              alt={image.alt ?? ""}
              focus={image.focus}
              fill
              preload={index === 0}
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-cover"
            />
          </SwiperSlide>
        ))}
      </Swiper>
    );

  return (
    <div className="relative aspect-732/706 mx-auto lg:max-h-full 2xl:max-h-[80vh]">
      <div
        className={`rounded-card absolute top-0 right-0 bottom-6 left-0 aspect-732/706 overflow-hidden 2xl:max-h-[80vh] ${border ? "border-hop border" : ""}`}
      >
        {slides}
      </div>
      {googleReview && (
        <div className="google-review-card fixed right-4 bottom-4 z-50 flex items-center gap-3 rounded-lg bg-white px-4 py-3 shadow-lg lg:absolute lg:right-0 lg:bottom-9 lg:z-10 lg:w-68.25 lg:rounded-r-none">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/icons/google.png" alt="Google" width={52} height={52} />
          <div className="flex flex-col gap-0.5">
            <p className="text-sm leading-tight font-semibold text-gray-800">
              {googleReview.name}
            </p>
            <div
              role="img"
              aria-label={`${googleReview.rating} out of 5 stars`}
              className="mb-1 flex gap-px"
            >
              {Array.from({ length: Math.round(googleReview.rating) }).map(
                (_, i) => (
                  <Icon
                    key={i}
                    name="star"
                    className="text-copper-kettle size-4"
                  />
                ),
              )}
            </div>
            <p className="text-xs text-gray-500">
              {googleReview.count} Google Reviews
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
